import { ActionButton, ToggleButton, ProgressCircle } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import ArrowUpSend from '@react-spectrum/s2/icons/ArrowUpSend';
import Close from '@react-spectrum/s2/icons/Close';
import Microphone from '@react-spectrum/s2/icons/Microphone';
import MicrophoneOff from '@react-spectrum/s2/icons/MicrophoneOff';
import StopProcessing from '@react-spectrum/s2/icons/StopProcessing';
import VolumeOff from '@react-spectrum/s2/icons/VolumeOff';
import VolumeTwo from '@react-spectrum/s2/icons/VolumeTwo';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import type { Editor } from '@tiptap/react';
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import {
	fileUploadService,
	getUploadErrorMessage,
	IMAGE_FILE_INPUT_ACCEPT,
} from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext.tsx';
import type { ChatService } from '@/bridge/ChatService.ts';
import { EditableText } from '@/components/EditableText/EditableText.tsx';
import {
	editorContentToTextWithLineBreaks,
	lineBreaksToHardBreaks,
} from '@/components/EditableText/tiptap-extensions.ts';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary.ts';
import { AddImageOverlay } from '@/spaces/chat/AddImageOverlay.tsx';
import {
	useNotifyUserInputStart,
	useRegisterChatInputSeedHandler,
} from '@/spaces/chat/ChatInputSeedContext.tsx';
import {
	MAX_PROMPT_IMAGES,
	PROMPT_IMAGE_CAP_MESSAGE,
	takeFilesForRemainingSlots,
} from '@/spaces/chat/promptAttachments.ts';
import { useImagePasteAndDrop } from '@/spaces/chat/useImagePasteAndDrop.ts';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

// Clipboard images on some platforms arrive as a File with an empty name,
// which the extension validator would reject with a nonsense ". not allowed"
// error. Give those files a name derived from their MIME type; a disallowed
// type (e.g. image/gif) still fails the MIME check with the right copy.
const ensureFileName = (file: File): File => {
	if (file.name) return file;
	return new File(
		[file],
		file.type === 'image/jpeg' ? 'pasted-image.jpg' : 'pasted-image.png',
		{ type: file.type },
	);
};

type PromptAttachment = {
	id: string;
	previewUrl: string;
	status: 'uploading' | 'completed' | 'error';
	url: string | null;
	// User-facing copy for a failed upload, shown next to the thumbnail.
	error: string | null;
};

interface ActionBarProps {
	enableAttachments?: boolean;
	enableAudioControls?: boolean;
	onAddAttachment?: () => void;
	chatService: ChatService;
	placeholder?: string;
	overlayPlaceholder?: string;
	isDisabled?: boolean;
	isGenerating?: boolean;
	showDisclaimer?: boolean;
}

export interface ActionBarHandle {
	focus: () => void;
}

export const ActionBar = forwardRef<ActionBarHandle, ActionBarProps>(
	(
		{
			enableAttachments = false,
			enableAudioControls = false,
			onAddAttachment,
			chatService,
			placeholder = 'Ask anything',
			overlayPlaceholder,
			isDisabled = false,
			isGenerating = false,
			showDisclaimer = true,
		},
		ref,
	) => {
		const bridgeApi = useBridgeApi();
		const userId = useUserId();
		const addImageToLibrary = useAddImageToLibrary(userId);

		const [hasText, setHasText] = useState(false);
		const notifyUserInputStart = useNotifyUserInputStart();
		const hasNotifiedInputStartRef = useRef(false);
		const [attachments, setAttachments] = useState<PromptAttachment[]>([]);
		// Cap-exceeded and validation messages, shown next to the thumbnails.
		const [attachmentNotice, setAttachmentNotice] = useState<string | null>(
			null,
		);

		const editorRef = useRef<Editor>(null);
		const fileInputRef = useRef<HTMLInputElement>(null);
		// Submit can fire from the editor's Enter handler, whose closure is
		// captured once when the tiptap editor is created — state reads inside it
		// are frozen at that point. Mirror every attachments update into this ref
		// so submit always sees the current list; render logic reads the state.
		const attachmentsRef = useRef<PromptAttachment[]>([]);
		const containerRef = useRef<HTMLDivElement>(null);
		const [flashSeq, setFlashSeq] = useState(0);

		const updateAttachments = useCallback(
			(updater: (prev: PromptAttachment[]) => PromptAttachment[]) => {
				setAttachments((prev) => {
					const next = updater(prev);
					attachmentsRef.current = next;
					return next;
				});
			},
			[],
		);

		useRegisterChatInputSeedHandler((text, opts) => {
			const editor = editorRef.current;
			if (editor) {
				// The editor stores line breaks as hardBreak nodes, not \n
				// characters, so seeded text needs the same conversion the
				// controlled-content path applies in TiptapEditor.
				editor.commands.setContent(lineBreaksToHardBreaks(text), {
					parseOptions: { preserveWhitespace: 'full' },
				});
				editor.commands.focus('end');
				setHasText(text.trim().length > 0);
			}
			if (opts?.flash) {
				setFlashSeq((s) => s + 1);
			}
		});

		useEffect(() => {
			if (flashSeq === 0) return;
			const el = containerRef.current;
			if (!el) return;
			el.classList.remove('animate-flash-accent');
			// Force a reflow so the animation restarts when flashSeq bumps again.
			void el.offsetWidth;
			el.classList.add('animate-flash-accent');
			const timer = window.setTimeout(() => {
				el.classList.remove('animate-flash-accent');
			}, 900);
			return () => window.clearTimeout(timer);
		}, [flashSeq]);

		useImperativeHandle(
			ref,
			() => ({
				focus: () => {
					if (!isDisabled) {
						editorRef.current?.commands.focus();
					}
				},
			}),
			[isDisabled],
		);

		const isSpeakerOn = chatService?.isSpeakerOn ?? false;
		const isMicOn = chatService?.isMicOn ?? false;
		const isSpeakerTransitioning = chatService?.isSpeakerTransitioning ?? false;
		const isMicTransitioning = chatService?.isMicTransitioning ?? false;

		const handleSubmit = useCallback(
			(textFromEnter?: string): boolean => {
				if (isDisabled) {
					// Visible feedback that the send was rejected; the editor keeps its
					// text because the Enter extension only clears on a `true` return.
					setFlashSeq((s) => s + 1);
					return false;
				}

				const currentAttachments = attachmentsRef.current;

				// Don't let a submit race an in-flight upload — the image would be
				// silently dropped. The send button is disabled in this state, but
				// Enter in the editor bypasses the button.
				if (currentAttachments.some((a) => a.status === 'uploading')) {
					return false;
				}

				const message =
					textFromEnter !== undefined
						? textFromEnter
						: editorRef.current
							? editorContentToTextWithLineBreaks(editorRef.current)
							: '';

				let didSend = false;

				// One bridge action per image, in tray order, ahead of the text
				// message so the backend reads the images before the prompt.
				const completedImageUrls = currentAttachments.flatMap((a) =>
					a.status === 'completed' && a.url !== null ? [a.url] : [],
				);

				for (const imageUrl of completedImageUrls) {
					try {
						trackEvent('Chat Image Uploaded');
						bridgeApi.sendAction({
							type: 'report_user_uploaded_image',
							image_url: imageUrl,
						});
						didSend = true;
					} catch (error) {
						console.error('Error sending uploaded image:', error);
					}
				}

				if (message.trim()) {
					trackEvent('Chat Message Sent', {
						has_image: completedImageUrls.length > 0,
						image_count: completedImageUrls.length,
						message_length: message.trim().length,
					});
					bridgeApi.sendAction({
						type: 'do_add_user_message_and_force_response',
						content: message.trim(),
					});
					didSend = true;
				} else if (completedImageUrls.length > 0) {
					// Image(s) with no text: the per-image actions above only record
					// the images, so the agent never takes a turn and the user gets no
					// reply. Force one turn (the text path above already does this).
					bridgeApi.sendAction({ type: 'do_force_agent_response' });
				}

				if (!didSend) {
					// Empty message and no image: nothing to send, nothing to clear.
					return false;
				}

				if (textFromEnter === undefined) {
					// Button path clears here; the Enter path is cleared by the
					// extension when this returns `true`.
					editorRef.current?.commands.clearContent();
				}

				updateAttachments(() => []);
				setAttachmentNotice(null);
				setHasText(false);
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
				return true;
			},
			[bridgeApi, isDisabled, updateAttachments],
		);

		const attachFile = useCallback(
			async (file: File) => {
				if (!userId) return;

				// The hidden input's `accept` attribute doesn't apply to paste or
				// drop, so this image validation is the gate for every entry path.
				const { previewUrl, error } =
					await fileUploadService.createImagePreview(file);
				if (!previewUrl) {
					setAttachmentNotice(error ?? 'Unable to attach this file');
					return;
				}

				const id = crypto.randomUUID();
				updateAttachments((prev) => [
					...prev,
					{ id, previewUrl, status: 'uploading', url: null, error: null },
				]);

				try {
					const result = await fileUploadService.upload(file, userId);
					updateAttachments((prev) =>
						prev.map((a) =>
							a.id === id ? { ...a, status: 'completed', url: result.url } : a,
						),
					);
					addImageToLibrary(result.url, 'chat');
				} catch (error) {
					console.error('Upload error:', error);
					updateAttachments((prev) =>
						prev.map((a) =>
							a.id === id
								? { ...a, status: 'error', error: getUploadErrorMessage(error) }
								: a,
						),
					);
				}
			},
			[addImageToLibrary, updateAttachments, userId],
		);

		// Shared by the file picker, paste, and drag-and-drop. Empty-named
		// clipboard files are renamed so the validator's copy stays sensible.
		const addFiles = useCallback(
			(files: File[]) => {
				if (isDisabled || !userId || files.length === 0) return;

				const named = files.map(ensureFileName);
				const { accepted, overflow } = takeFilesForRemainingSlots(
					named,
					attachmentsRef.current.length,
				);
				if (overflow > 0) {
					setAttachmentNotice(PROMPT_IMAGE_CAP_MESSAGE);
				}

				// Uploads run concurrently, one promise per file, so one failure
				// only marks its own attachment as errored.
				for (const file of accepted) {
					void attachFile(file);
				}
			},
			[attachFile, isDisabled, userId],
		);

		const handleFileSelect = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				addFiles(Array.from(event.target.files ?? []));
				// Reset so picking the same file again re-fires the change event.
				if (event.target) {
					event.target.value = '';
				}
			},
			[addFiles],
		);

		// When onAddAttachment is set, the host owns attachment handling and the
		// internal pipeline is bypassed — paste/drop must respect that opt-out.
		// Drops only engage over the prompt bar container so in-chat drop
		// targets (e.g. reference-image card slots) keep working.
		const { isDragActive } = useImagePasteAndDrop({
			enabled: enableAttachments && !onAddAttachment && !isDisabled,
			onFiles: addFiles,
			dropTargetRef: containerRef,
		});

		const handleRemoveAttachment = useCallback(
			(id: string) => {
				updateAttachments((prev) => prev.filter((a) => a.id !== id));
				setAttachmentNotice(null);
				if (fileInputRef.current) {
					fileInputRef.current.value = '';
				}
			},
			[updateAttachments],
		);

		const handleStop = useCallback(() => {
			bridgeApi.sendAction({ type: 'do_stop_generation' });
		}, [bridgeApi]);

		const handleAttachmentClick = useCallback(() => {
			if (onAddAttachment) {
				onAddAttachment();
			} else {
				fileInputRef.current?.click();
			}
		}, [onAddAttachment]);

		const isUploadingAttachment = attachments.some(
			(a) => a.status === 'uploading',
		);
		const hasCompletedAttachment = attachments.some(
			(a) => a.status === 'completed' && a.url !== null,
		);
		const canSend =
			(hasText || hasCompletedAttachment) && !isUploadingAttachment;

		return (
			<div className="flex flex-col gap-2">
				{(attachments.length > 0 || attachmentNotice) && (
					<div className="flex flex-col gap-2 px-2">
						{attachments.length > 0 && (
							<div className="flex flex-wrap items-center gap-2">
								{attachments.map((attachment, index) => (
									<div key={attachment.id} className="flex items-center gap-2">
										<div className="relative">
											<img
												src={attachment.previewUrl}
												alt="Upload preview"
												className="h-16 w-16 rounded-lg object-cover"
											/>
											{attachment.status === 'uploading' && (
												<div
													className="absolute inset-0 flex items-center
														justify-center rounded-lg bg-black/50"
												>
													<ProgressCircle isIndeterminate size="S" />
												</div>
											)}
											<div
												className="absolute top-0 right-0 translate-x-1/2
													-translate-y-1/2 overflow-clip rounded-full"
											>
												<ActionButton
													size="XS"
													onPress={() => handleRemoveAttachment(attachment.id)}
													aria-label={`Remove attachment ${index + 1}`}
													UNSAFE_className="rounded-full"
												>
													<Close />
												</ActionButton>
											</div>
										</div>
										{attachment.status === 'error' && (
											<span className="text-xs text-red-500">
												{attachment.error ?? 'Upload failed. Please try again.'}
											</span>
										)}
									</div>
								))}
							</div>
						)}
						{attachmentNotice && (
							<span className="text-xs text-red-500">{attachmentNotice}</span>
						)}
					</div>
				)}

				<div
					ref={containerRef}
					data-testid="action-bar"
					className={cn(
						'relative flex flex-col gap-2 rounded-2xl p-3',
						style({
							backgroundColor: 'layer-2',
							boxShadow: 'elevated',
						}),
					)}
				>
					{isDragActive && <AddImageOverlay />}
					<input
						ref={fileInputRef}
						type="file"
						accept={IMAGE_FILE_INPUT_ACCEPT}
						multiple
						className="hidden"
						onChange={handleFileSelect}
					/>

					<div className="relative min-w-0">
						<EditableText
							text=""
							placeholder=""
							containerClassName="max-h-24 overflow-auto"
							editorClassName={cn(
								'w-full border-0 focus:outline-none',
								'text-black caret-black',
								'text-[18px] md:text-base',
								isDisabled && 'pointer-events-none opacity-50',
							)}
							onEnter={handleSubmit}
							onEdit={(text) => {
								const trimmed = text.trim();
								setHasText(!!trimmed);
								if (trimmed && !hasNotifiedInputStartRef.current) {
									hasNotifiedInputStartRef.current = true;
									notifyUserInputStart();
								}
							}}
							editorRef={editorRef}
						/>
						{!hasText && (
							<div
								aria-hidden="true"
								className="pointer-events-none absolute inset-0 text-[18px]
									text-(--text-muted) md:text-base"
							>
								{overlayPlaceholder ?? placeholder}
							</div>
						)}
					</div>

					<div className="flex w-full items-center justify-between">
						<div className="flex items-center gap-2">
							{enableAudioControls && (
								<>
									<ToggleButton
										isSelected={isSpeakerOn}
										onChange={() => {
											if (chatService) {
												chatService.toggleSpeaker();
											}
										}}
										aria-label="Toggle speaker"
										isDisabled={isDisabled || isSpeakerTransitioning}
										UNSAFE_className={cn(
											'h-6 rounded-[7px] !px-1',
											style({ backgroundColor: 'gray-100' }),
										)}
									>
										<div className="flex h-4 w-4 items-center justify-center">
											{isSpeakerTransitioning ? (
												<ProgressCircle isIndeterminate size="S" />
											) : isSpeakerOn ? (
												<VolumeTwo />
											) : (
												<VolumeOff />
											)}
										</div>
									</ToggleButton>
									<ToggleButton
										isSelected={isMicOn}
										onChange={() => {
											if (chatService) {
												chatService.toggleMic();
											}
										}}
										aria-label="Toggle microphone"
										isDisabled={isDisabled || isMicTransitioning}
										UNSAFE_className={cn(
											'h-6 rounded-[7px] !px-1',
											style({ backgroundColor: 'gray-100' }),
										)}
									>
										<div className="flex h-4 w-4 items-center justify-center">
											{isMicTransitioning ? (
												<ProgressCircle isIndeterminate size="S" />
											) : isMicOn ? (
												<Microphone />
											) : (
												<MicrophoneOff />
											)}
										</div>
									</ToggleButton>
								</>
							)}
							{enableAttachments && (
								<ActionButton
									isQuiet
									isDisabled={
										isDisabled || attachments.length >= MAX_PROMPT_IMAGES
									}
									aria-label="Add attachment"
									onPress={handleAttachmentClick}
									UNSAFE_className="h-6 rounded-[7px] !px-1"
								>
									<Add />
								</ActionButton>
							)}
						</div>
						<div className="flex items-center gap-2">
							{isGenerating ? (
								<button
									type="button"
									onClick={handleStop}
									aria-label="Stop generating"
									className="flex h-8 w-8 items-center justify-center
										rounded-full bg-gray-900 text-white transition-colors
										hover:bg-gray-700"
								>
									<span
										className="flex h-[18px] w-[18px] items-center
											justify-center"
										style={{ '--iconPrimary': 'white' } as React.CSSProperties}
									>
										<StopProcessing />
									</span>
								</button>
							) : (
								<button
									type="button"
									onClick={() => void handleSubmit()}
									disabled={!canSend || isDisabled}
									aria-label="Send message"
									className={cn(
										`flex h-8 w-8 items-center justify-center rounded-full
											transition-colors`,
										canSend
											? 'bg-gray-900 text-white hover:bg-gray-700'
											: 'cursor-not-allowed bg-gray-100 text-gray-400',
									)}
								>
									<span
										className="flex h-[18px] w-[18px] items-center
											justify-center"
										style={
											{
												'--iconPrimary': canSend ? 'white' : 'rgb(107 114 128)',
											} as React.CSSProperties
										}
									>
										<ArrowUpSend />
									</span>
								</button>
							)}
						</div>
					</div>
				</div>
				<div
					className="grid overflow-hidden
						transition-[grid-template-rows,opacity] duration-300 ease-in-out"
					style={{
						gridTemplateRows: showDisclaimer ? '1fr' : '0fr',
						opacity: showDisclaimer ? 1 : 0,
					}}
					aria-hidden={!showDisclaimer}
				>
					<div className="min-h-0">
						<div
							className="flex items-baseline justify-center gap-1 text-xs
								font-normal text-gray-500"
						>
							<span>Bridgeway is AI. Verify its responses.</span>
							<a
								href="https://www.adobe.com/legal/licenses-terms/adobe-gen-ai-user-guidelines.html"
								target="_blank"
								rel="noreferrer"
								className="underline decoration-gray-500"
							>
								AI Guidelines
							</a>
						</div>
					</div>
				</div>
			</div>
		);
	},
);

ActionBar.displayName = 'ActionBar';
